import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPANY_AUTH } from "@/constants/testIds";
import { toast } from "sonner";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", admin_name: "", admin_email: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-company", { ...form, accepted_terms: acceptedTerms });
      toast.success(data.message);
      if (data.dev_verification_link) {
        setDevLink(data.dev_verification_link);
      } else {
        navigate("/login");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <ShieldCheck className="w-7 h-7 text-petroleum" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-charcoal">CERH</span>
        </Link>
        <div className="bg-card border border-petroleum/10 rounded-2xl p-8 shadow-[0_8px_30px_rgba(14,79,79,0.08)]">
          <h1 className="font-heading text-2xl font-bold text-charcoal mb-1">Registra tu empresa</h1>
          <p className="text-sm text-charcoal/60 mb-6">Te mandamos un correo para confirmar tu cuenta. Ahí sigues con crear tu contraseña y activar tu plan.</p>

          {devLink ? (
            <div data-testid={COMPANY_AUTH.devVerificationBanner} className="bg-accent border border-terracotta/30 rounded-xl p-4 text-sm">
              <p className="font-semibold text-charcoal mb-2">Modo de desarrollo (Resend no configurado)</p>
              <p className="text-charcoal/70 mb-3">Usa este enlace para confirmar tu correo:</p>
              <a href={devLink} className="text-petroleum font-medium underline break-all">{devLink}</a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nombre de la empresa</Label>
                <Input data-testid={COMPANY_AUTH.companyNameInput} required value={form.company_name} onChange={update("company_name")} placeholder="Grupo Ejemplo S.A. de C.V." />
              </div>
              <div>
                <Label>Tu nombre</Label>
                <Input data-testid={COMPANY_AUTH.adminNameInput} required value={form.admin_name} onChange={update("admin_name")} placeholder="Nombre completo" />
              </div>
              <div>
                <Label>Correo</Label>
                <Input data-testid={COMPANY_AUTH.adminEmailInput} type="email" required value={form.admin_email} onChange={update("admin_email")} placeholder="tu@empresa.mx" />
              </div>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <Checkbox
                  data-testid={COMPANY_AUTH.acceptTermsCheckbox}
                  checked={acceptedTerms}
                  onCheckedChange={(v) => setAcceptedTerms(!!v)}
                  className="mt-0.5"
                />
                <span className="text-charcoal/70">
                  Acepto los{" "}
                  <Link to="/terminos" target="_blank" rel="noopener noreferrer" className="text-petroleum font-medium underline">
                    Términos de Uso
                  </Link>{" "}
                  y el{" "}
                  <Link to="/aviso-privacidad" target="_blank" rel="noopener noreferrer" className="text-petroleum font-medium underline">
                    Aviso de Privacidad
                  </Link>{" "}
                  de CERH.
                </span>
              </label>
              <Button data-testid={COMPANY_AUTH.submitButton} type="submit" disabled={loading || !acceptedTerms} className="w-full bg-petroleum hover:bg-petroleum/90 rounded-full h-11 disabled:opacity-50">
                {loading ? "Registrando..." : "Registrar empresa"}
              </Button>
            </form>
          )}
          <p className="text-sm text-center text-charcoal/60 mt-6">
            ¿Ya tienes cuenta? <Link data-testid={COMPANY_AUTH.loginLink} to="/login" className="text-petroleum font-semibold">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
